/**
 * Types for Sources admin UI
 */

export type SourceCategory = 'prep_resource' | 'interview_experience'

// Free text (normalized lowercase server-side); the legacy six values are
// offered as <datalist> suggestions in the editor, not enforced as an enum.
export type SourceContentType = string

export type SourceContentItemType =
  | 'PROBLEM'
  | 'CODING_EXERCISE'
  | 'SYSTEM_DESIGN'
  | 'TASK'
  | 'VIDEO'
  | 'ARTICLE'
  | 'RESOURCE'

export interface Source {
  id: string
  name: string
  slug: string
  icon: string | null
  category: SourceCategory
  url: string | null
  // When set, `name` above is derived from the company rather than stored on
  // the Source row — it is a display value and must not be written back.
  companyId: string | null
  // Publishing control: false for external sources (LeetCode), true for original content (BCTCI)
  isPublishable: boolean
  createdAt: string
  updatedAt: string
}

export interface SourceContent {
  id: string
  sourceId: string
  title: string
  slug: string
  description: string | null
  kind: SourceContentType
  actionVerb: string | null
  order: number
  url: string | null
  parentId: string | null
  estimatedMinutes: number | null
  icon: string | null // Icon override (emoji, URL, or filename)
  createdAt: string
  updatedAt: string
  itemCount?: number // Count of items (problems, exercises, etc.) in this content
  items?: SourceContentItem[]
  children?: SourceContentChild[]
}

export interface SourceContentChild {
  id: string
  title: string
  slug: string
  kind: SourceContentType
  actionVerb: string | null
  order: number
  description: string | null
  icon: string | null
  estimatedMinutes: number | null
  itemCount: number
  createdAt: string
}

export interface SourceContentItem {
  id: string
  sourceContentId: string
  contentType: SourceContentItemType
  contentId: string | null // ID of Problem/CodingExercise/SystemDesignProblem/Task
  title: string | null // For external items (VIDEO, ARTICLE)
  url: string | null // For external items
  description: string | null
  order: number
  durationMinutes: number | null // For videos
  icon: string | null // Icon override (emoji, URL, or filename)
  createdAt: string
  updatedAt: string
  // Populated content (optional, enriched by API)
  problem?: {
    id: string
    title: string
    slug?: string
    difficulty?: string | null
  } | null
  codingExercise?: {
    id: string
    title: string
    slug?: string
    difficulty?: string | null
  } | null
  systemDesignProblem?: {
    id: string
    title: string
    slug?: string
    difficulty?: string | null
  } | null
  task?: {
    id: string
    title: string
    category?: string | null
  } | null
  resource?: {
    id: string
    title: string
    url: string
    type: string
    durationMinutes?: number | null
    icon?: string | null
  } | null
}

export interface SourceFormData {
  name: string
  slug?: string
  icon?: string | null
  category: SourceCategory
  url?: string | null
  isPublishable?: boolean
}

export interface SourceContentFormData {
  sourceId: string
  title: string
  description?: string | null
  kind: SourceContentType
  actionVerb?: string | null
  order?: number
  url?: string | null
  parentId?: string | null
  estimatedMinutes?: number | null
  icon?: string | null
}

export const SOURCE_CATEGORIES: { value: SourceCategory; label: string }[] = [
  { value: 'prep_resource', label: 'Prep Resource' },
  { value: 'interview_experience', label: 'Interview Experience' },
]

export const SOURCE_CONTENT_TYPES: {
  value: SourceContentType
  label: string
}[] = [
  { value: 'chapter', label: 'Chapter' },
  { value: 'module', label: 'Module' },
  { value: 'playlist', label: 'Playlist' },
  { value: 'series', label: 'Series' },
  { value: 'section', label: 'Section' },
  { value: 'lesson', label: 'Lesson' },
]

export const SOURCE_CONTENT_ITEM_TYPES: {
  value: SourceContentItemType
  label: string
  isInternal: boolean // true = links to internal content, false = external (VIDEO, ARTICLE)
}[] = [
  { value: 'PROBLEM', label: 'Coding Problem', isInternal: true },
  { value: 'CODING_EXERCISE', label: 'Coding Exercise', isInternal: true },
  { value: 'SYSTEM_DESIGN', label: 'System Design', isInternal: true },
  { value: 'TASK', label: 'Task', isInternal: true },
  { value: 'VIDEO', label: 'Video', isInternal: false },
  { value: 'ARTICLE', label: 'Article', isInternal: false },
  { value: 'RESOURCE', label: 'Resource', isInternal: true },
]

/**
 * Content item kinds that can appear in a listSourceContent summary on read.
 * VIDEO/ARTICLE are create-only inputs (they resolve to RESOURCE rows) and
 * never appear as summary keys.
 *
 * Derived rather than restated: a hand-maintained copy of this union meant a
 * kind added to `SourceContentItemType` was simply absent here, and the
 * Content Summary widget dropped it without any error. `Exclude` makes the
 * default the visible one — a new kind shows up in the summary until someone
 * deliberately marks it create-only.
 */
export type SourceContentSummaryKind = Exclude<
  SourceContentItemType,
  'VIDEO' | 'ARTICLE'
>

/**
 * Summary of content item counts by type for a source
 */
export type SourceContentSummary = Partial<
  Record<SourceContentSummaryKind, number>
>
